import type { FC } from "react";

import type { ComponentIconProps } from "@similarweb/icons";
import { LockOpen, SidebarRocket } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { IconButton } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

/**
 *  HDBanner is not rendered in the normal React tree, so useTranslation has no i18n provider to read from.
 *  translate from i18nFilter talks to the global window.i18n
 *  directly and works in that one-shot render
 */
import { translate } from "app/filters/i18nFilter";
import {
    BannerWrapper,
    BannerRow,
    BannerText,
    BannerCta,
} from "app/marketing/components/common/banners/HDBanner/HDBanner.styled";
import { shouldShowHDBanner } from "app/marketing/components/common/banners/HDBanner/shouldShowHDBanner";
import type { IHDBannerCtaState } from "app/marketing/components/common/banners/HDBanner/useHDBannerCta";

/**
 * Highcharts sanitizes the tooltip markup and keeps only a fixed set of attributes (`class`, `id`,
 * `type`, `style`, ...), so `data-*` hooks are dropped. The delegated click has to match on a class.
 */
export const HD_BANNER_CTA_CLASS = "hd-banner-cta";
export const HD_BANNER_CTA_SELECTOR = `.${HD_BANNER_CTA_CLASS}`;

interface ICtaContent {
    ctaTranslationKey: string;
    descriptionTranslationKey: string;
    icon: FC<ComponentIconProps> | null;
}

const getCtaContent = ({ canPurchaseAddon, canUpgradeAddon }: IHDBannerCtaState): ICtaContent => {
    if (canPurchaseAddon) {
        return {
            ctaTranslationKey: "marketing.addon.hd_banner.unlock_cta",
            descriptionTranslationKey: "marketing.addon.hd_banner.unlock_description",
            icon: LockOpen,
        };
    }

    if (canUpgradeAddon) {
        return {
            ctaTranslationKey: "marketing.addon.hd_banner.upgrade_cta",
            descriptionTranslationKey: "marketing.addon.hd_banner.upgrade_description",
            icon: SidebarRocket,
        };
    }

    return {
        ctaTranslationKey: "marketing.addon.hd_banner.talk_to_us_cta",
        descriptionTranslationKey: "marketing.addon.hd_banner.talk_to_us_description",
        icon: null,
    };
};

interface IHDBannerProps {
    ctaState: IHDBannerCtaState;
    dataAutomation?: string;
}

export const HDBanner = ({ ctaState, dataAutomation = "hd-banner-cta" }: IHDBannerProps) => {
    if (!shouldShowHDBanner()) {
        return null;
    }

    const { isLoading } = ctaState;
    const { ctaTranslationKey, descriptionTranslationKey, icon: CtaIcon } = getCtaContent(ctaState);

    return (
        <BannerWrapper $isLoading={isLoading}>
            <BannerRow>
                {isLoading ? (
                    <IconButton
                        iconSize="xs"
                        iconName="download"
                        type="flat"
                        isLoading={true}
                    />
                ) : (
                    <>
                        <BannerText
                            fontVariant="body2"
                            color={colorsPalettes.carbon[400]}
                        >
                            {translate(descriptionTranslationKey)}
                        </BannerText>
                        <BannerCta
                            type="outlined"
                            className={HD_BANNER_CTA_CLASS}
                            dataAutomation={dataAutomation}
                        >
                            {CtaIcon ? (
                                <CtaIcon
                                    size="xs"
                                    color={colorsPalettes.blue[400]}
                                />
                            ) : null}
                            <Text
                                fontVariant="button"
                                color={colorsPalettes.blue[400]}
                            >
                                {translate(ctaTranslationKey)}
                            </Text>
                        </BannerCta>
                    </>
                )}
            </BannerRow>
        </BannerWrapper>
    );
};
